import React from 'react';
import { Card } from 'antd';
import { SkBar, SkTabsBar } from './_primitives';

const PointsHeader = () => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 sm:mb-6">
    <div className="flex flex-col gap-2 min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <SkBar h="h-3" w="w-16" />
        <SkBar h="h-3" w="w-2" />
        <SkBar h="h-3" w="w-12" />
      </div>
      <SkBar h="h-7" w="w-64" className="max-w-full mt-1" />
      <SkBar h="h-3" w="w-80" className="max-w-full" />
    </div>
    <SkBar h="h-9" w="w-32" className="rounded-md flex-shrink-0" />
  </div>
);

const StatCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-11 h-11 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-3" w="w-28" />
        <SkBar h="h-6" w="w-24" />
        <SkBar h="h-2" w="w-16" />
      </div>
    </div>
  </Card>
);

const QuickActionButton = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 14 } }}>
    <div className="flex items-center justify-center gap-2">
      <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
      <SkBar h="h-3" w="w-28" />
    </div>
  </Card>
);

const FilterBar = () => (
  <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:items-center sm:justify-between">
    <SkBar h="h-10" w="w-full sm:w-80 sm:max-w-md" className="rounded-md" />
    <div className="flex items-center gap-3 flex-shrink-0">
      <SkBar h="h-3" w="w-32" />
      <SkBar h="h-10" w="w-24" className="rounded-md" />
    </div>
  </div>
);

const COL_WIDTHS = ['w-12', 'w-12', 'w-16', 'w-20', 'w-12', 'w-14'];

const TransactionRow = () => (
  <div className="grid grid-cols-[2fr_1.5fr_1fr_2fr_1.5fr_0.6fr] gap-3 py-3 border-b border-gray-100 last:border-b-0 items-center">
    {/* User: avatar + name + email */}
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <SkBar h="h-3" w="w-24" />
        <SkBar h="h-2" w="w-32" />
      </div>
    </div>
    {/* Type: icon + label */}
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-5 h-5 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-3" w="w-24" />
    </div>
    {/* Amount */}
    <SkBar h="h-3" w="w-12" />
    {/* Description */}
    <SkBar h="h-3" w="w-32" />
    {/* Date */}
    <SkBar h="h-3" w="w-28" />
    {/* Actions (vertical dots) */}
    <SkBar h="h-6" w="w-6" className="rounded-md justify-self-end" />
  </div>
);

const TablePagination = () => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 mt-4 pt-3">
    <SkBar h="h-3" w="w-40" />
    <div className="flex items-center gap-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <SkBar key={i} h="h-8" w="w-8" className="rounded-md" />
      ))}
    </div>
    <SkBar h="h-8" w="w-24" className="rounded-md" />
  </div>
);

/**
 * PointsPageLoader — matches Points Management layout:
 *  breadcrumb + header with award button · 4 stat cards (icon left) ·
 *  4 quick-action buttons · card with 6 tabs · search + filter ·
 *  transaction table · pagination.
 */
const PointsPageLoader = () => (
  <div className="w-full p-3 sm:p-4 md:p-6">
    <PointsHeader />

    {/* 4 stat cards */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatCard key={i} />
      ))}
    </div>

    {/* 4 quick-action buttons */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <QuickActionButton key={i} />
      ))}
    </div>

    {/* Transactions card */}
    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <SkTabsBar tabs={6} />
      <FilterBar />
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Table header */}
          <div className="grid grid-cols-[2fr_1.5fr_1fr_2fr_1.5fr_0.6fr] gap-3 pb-3 border-b border-gray-200">
            {COL_WIDTHS.map((w, i) => (
              <SkBar
                key={i}
                h="h-3"
                w={w}
                className={i === COL_WIDTHS.length - 1 ? 'justify-self-end' : ''}
              />
            ))}
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <TransactionRow key={i} />
          ))}
        </div>
      </div>
      <TablePagination />
    </Card>
  </div>
);

export default PointsPageLoader;
